import { z } from 'zod';
import { USER_ROLES } from '../domain';
import { tenantCodeSchema } from './tenant';

export const loginRequestSchema = z.object({
  tenantCode: tenantCodeSchema,
  email: z.string().trim().email().max(254),
  password: z.string().min(1).max(128),
  /** Requis si le TOTP est activé pour le compte. */
  totp: z
    .string()
    .regex(/^\d{6}$/)
    .optional(),
});

export const sessionUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: z.enum(USER_ROLES),
  tenantId: z.string(),
  /** Administrateur sans TOTP : seule la configuration du TOTP est accessible (BA-045). */
  mfaSetupRequired: z.boolean(),
});

export const sessionResponseSchema = z.object({
  accessToken: z.string(),
  tokenType: z.literal('Bearer'),
  expiresIn: z.number().int(),
  user: sessionUserSchema,
});

export const acceptInvitationSchema = z.object({
  token: z.string().min(1).max(200),
  password: z.string().min(1).max(128),
});

export const invitationRequestSchema = z.object({
  email: z.string().trim().email().max(254),
  name: z.string().min(1).max(120),
  role: z.enum(USER_ROLES),
});

export const invitationResponseSchema = z.object({
  userId: z.string(),
  /** À transmettre à l'invité tant que l'envoi par email n'est pas branché (ADR-016). */
  invitationToken: z.string(),
  expiresAt: z.string(),
});

/** Préférences d'affichage du portail, propres à chaque utilisateur. */
export const userPreferencesSchema = z.object({
  language: z.enum(['fr', 'en', 'ar']),
  theme: z.enum(['light', 'dark', 'system']),
  sidebarCollapsed: z.boolean(),
});
export type UserPreferences = z.infer<typeof userPreferencesSchema>;
export const DEFAULT_PREFERENCES: UserPreferences = {
  language: 'fr',
  theme: 'system',
  sidebarCollapsed: false,
};

export const profilePatchSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    preferences: userPreferencesSchema.partial(),
  })
  .partial()
  .strict();

export const passwordChangeSchema = z.object({
  currentPassword: z.string().min(1).max(128),
  newPassword: z.string().min(1).max(128),
});

export const meResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  role: z.enum(USER_ROLES),
  totpEnabled: z.boolean(),
  tenant: z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    /** Statut de l'abonnement (bandeau du portail) ; nul pour un cabinet historique. */
    subscriptionStatus: z
      .enum(['trial', 'active', 'past_due', 'suspended', 'cancelled'])
      .nullable()
      .optional(),
  }),
  preferences: userPreferencesSchema,
});

export const totpSetupResponseSchema = z.object({
  secret: z.string(),
  otpauthUri: z.string(),
});

export const totpCodeSchema = z.object({ code: z.string().regex(/^\d{6}$/) });
